import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faComments, faCalendarAlt, faStar, faUserTie,
  faFileAlt, faFilter, faFileExcel, faFilePdf, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import axios from 'axios';
import moment from 'moment';
import XLSX from 'xlsx-js-style';
import stateDistrictTalukaData from '../../assets/static/IndiaStateDistrictTaluka';

// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  dark: '#1a3328', mid: '#2d5a3d', bright: '#3d8c5e',
  light: '#e8f5ee', page: '#f4f6f5', border: '#d8e8dc',
  white: '#ffffff', textDk: '#1a2e22', textGr: '#6c757d',
  active: '#e8f5ee', activeBorder: '#2d5a3d',
};

// ─── Menu definition ──────────────────────────────────────────────────────────
const MENU_ITEMS = [
  { id: 'farmers',     label: 'Farmers',     icon: faUsers       },
  { id: 'queries',     label: 'Queries',     icon: faComments    },
  { id: 'appointment', label: 'Appointment', icon: faCalendarAlt },
  { id: 'feedback',    label: 'Feedback',    icon: faStar        },
  { id: 'advisor',     label: 'Advisor',     icon: faUserTie     },
];

// ─── Column definitions ───────────────────────────────────────────────────────
const FARMER_COLS = [
  { key: 'regNo',                  label: 'Farmer ID' },
  { key: 'fullName',               label: 'Farmer Name' },
  { key: 'mobileNumber',           label: 'Mobile' },
  { key: 'townVillage',            label: 'Village' },
  { key: 'taluka',                 label: 'Taluka' },
  { key: 'district',               label: 'District' },
  { key: 'state',                  label: 'State' },
  { key: 'currentCropsCultivated', label: 'Crop' },
  { key: 'landAcreage',            label: 'Land (Acres)' },
  { key: 'farmerCategory',         label: 'Category' },
  { key: 'status',                 label: 'Status' },
  { key: 'heardAbout',             label: 'Heard About' },
  { key: 'createdAt',              label: 'Registered On', isDate: true },
];

const QUERY_COLS = [
  { key: 'issueCode',      label: 'Query Code' },
  { key: 'farmerName',     label: 'Farmer Name' },
  { key: 'farmerMobile',   label: 'Mobile' },
  { key: 'townVillage',    label: 'Village' },
  { key: 'taluka',         label: 'Taluka' },
  { key: 'district',       label: 'District' },
  { key: 'state',          label: 'State' },
  { key: 'issueCrop',      label: 'Crop' },
  { key: 'season',         label: 'Season' },
  { key: 'issueCategory',  label: 'Category' },
  { key: 'issueStatus',    label: 'Status' },
  { key: 'issueAssignee',  label: 'Assigned To' },
  { key: 'issueCreatedAt', label: 'Date', isDate: true },
];

const APPOINTMENT_COLS = [
  { key: 'appointmentId',   label: 'Appt. ID' },
  { key: 'farmerId',        label: 'Farmer ID' },
  { key: 'farmerName',      label: 'Farmer Name' },
  { key: 'farmerMobile',    label: 'Mobile' },
  { key: 'staff',           label: 'Staff' },
  { key: 'crop',            label: 'Crop' },
  { key: 'village',         label: 'Village' },
  { key: 'taluka',          label: 'Taluka' },
  { key: 'district',        label: 'District' },
  { key: 'state',           label: 'State' },
  { key: 'status',          label: 'Status' },
  { key: 'appointmentDate', label: 'Appt. Date', isDate: true },
];

const FEEDBACK_COLS = [
  { key: 'feedbackId',   label: 'Feedback ID' },
  { key: 'farmerId',     label: 'Farmer ID' },
  { key: 'farmerName',   label: 'Farmer Name' },
  { key: 'farmerMobile', label: 'Mobile' },
  { key: 'category',     label: 'Category' },
  { key: 'village',      label: 'Village' },
  { key: 'taluka',       label: 'Taluka' },
  { key: 'district',     label: 'District' },
  { key: 'state',        label: 'State' },
  { key: 'advisor',      label: 'Advisor' },
  { key: 'createdAt',    label: 'Date', isDate: true },
];

const ADVISOR_COLS = [
  { key: 'advisorName',       label: 'Advisor Name' },
  { key: 'mobileNumber',      label: 'Mobile' },
  { key: 'queriesResolved',   label: 'Queries Resolved' },
  { key: 'appointmentsTaken', label: 'Appointments Taken' },
  { key: 'feedbackGiven',     label: 'Feedback Given' },
  { key: 'farmersAdded',      label: 'Farmers Added' },
  { key: 'totalActivity',     label: 'Total Activity' },
];

const MENU_COLUMNS = {
  farmers:     FARMER_COLS,
  queries:     QUERY_COLS,
  appointment: APPOINTMENT_COLS,
  feedback:    FEEDBACK_COLS,
  advisor:     ADVISOR_COLS,
};

const PAGE_SIZE = 20;

// ─── Style constants ──────────────────────────────────────────────────────────
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#1a2e22', marginBottom: 4, display: 'block' };
const selectStyles = {
  control: (b) => ({ ...b, minHeight: 32, fontSize: '0.82rem' }),
  menu: (b) => ({ ...b, fontSize: '0.82rem', zIndex: 9999 }),
};

// ─── Component ────────────────────────────────────────────────────────────────
function ReportCenter() {
  const [activeMenu, setActiveMenu] = useState(null);
  const [filters, setFilters] = useState({});
  const [districtOpts, setDistrictOpts] = useState([]);
  const [talukaOpts, setTalukaOpts] = useState([]);
  const [operatorOpts, setOperatorOpts] = useState([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/user/getUsers`)
      .then(res => {
        const opts = (res.data || [])
          .filter(u => u.status === 'active')
          .map(u => ({ value: u.fullName, label: u.fullName }));
        setOperatorOpts(opts);
      })
      .catch(() => {});
  }, []);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const menuMeta = MENU_ITEMS.find(m => m.id === activeMenu) || null;
  const columns = activeMenu ? (MENU_COLUMNS[activeMenu] || []) : [];

  const handleMenuSelect = (menuId) => {
    setActiveMenu(menuId);
    setFilters({});
    setDistrictOpts([]);
    setTalukaOpts([]);
    setResults(null);
    setError(null);
    setCurrentPage(1);
  };

  const setFilter = (key, value) => setFilters(p => ({ ...p, [key]: value }));

  const handleStateChange = (opt) => {
    setFilters(p => ({ ...p, state: opt ? opt.value : '', district: '', taluka: '' }));
    setDistrictOpts(opt ? Object.keys(stateDistrictTalukaData[opt.value] || {}).map(d => ({ value: d, label: d })) : []);
    setTalukaOpts([]);
  };

  const handleDistrictChange = (opt) => {
    setFilters(p => ({ ...p, district: opt ? opt.value : '', taluka: '' }));
    if (opt && filters.state) {
      const ts = stateDistrictTalukaData[filters.state]?.[opt.value] || [];
      setTalukaOpts(ts.map(t => ({ value: t, label: t })));
    } else {
      setTalukaOpts([]);
    }
  };

  const stateOpts = Object.keys(stateDistrictTalukaData).map(s => ({ value: s, label: s }));

  // ── Generate report ─────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!activeMenu) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentPage(1);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reports/generate`, {
        reportCategory: activeMenu,
        filters,
      });
      if (res.data.success) {
        setResults(res.data.data);
      } else {
        setError(res.data.message || 'Failed to generate report.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Excel export ─────────────────────────────────────────────────────────────
  const getBorder = () => ({
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } },
  });

  const handleExportExcel = () => {
    if (!results || results.length === 0) { alert('No data to export.'); return; }
    const wb = XLSX.utils.book_new();
    const wsData = [
      columns.map(c => ({ v: c.label, s: { font: { bold: true }, border: getBorder() } })),
      ...results.map(row =>
        columns.map(c => {
          const val = c.isDate ? (row[c.key] ? moment(row[c.key]).format('DD-MMM-YYYY') : '') : (row[c.key] ?? '');
          return { v: String(val), s: { border: getBorder() } };
        })
      ),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = wsData[0].map((_, i) => ({
      wch: Math.max(...wsData.map(r => (r[i] && r[i].v ? String(r[i].v).length : 0))) + 2,
    }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2' };
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${menuMeta?.label || 'report'}_${moment().format('DDMMMYYYY')}.xlsx`);
  };

  // ── PDF export (print) ───────────────────────────────────────────────────────
  const handleExportPdf = () => {
    if (!results || results.length === 0) { alert('No data to export.'); return; }
    const rows = results.map(row =>
      `<tr>${columns.map(c => `<td>${cellVal(row, c)}</td>`).join('')}</tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>${menuMeta?.label} Report</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 10px; margin: 20px; }
        h3 { color: #29553D; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #999; padding: 3px 6px; text-align: left; }
        th { background-color: #29553D; color: white; font-weight: bold; }
        tr:nth-child(even) { background-color: #f5f5f5; }
        @media print { body { margin: 10mm; } }
      </style>
    </head><body>
      <h3>${menuMeta?.label} Report — ${moment().format('DD MMM YYYY')}</h3>
      <table>
        <thead><tr>${columns.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  // ── Cell value ───────────────────────────────────────────────────────────────
  const cellVal = (row, col) => {
    const v = row[col.key];
    if (v == null || v === '') return '—';
    if (col.isDate) return moment(v).isValid() ? moment(v).format('DD-MMM-YYYY') : v;
    return String(v);
  };

  // ── Pagination ───────────────────────────────────────────────────────────────
  const totalPages = results ? Math.ceil(results.length / PAGE_SIZE) : 0;
  const pageRows = results ? results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) : [];

  // ── Shared filter blocks ─────────────────────────────────────────────────────
  const renderLocationFilters = () => (
    <>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>State</Form.Label>
        <Select
          placeholder="Select State"
          options={stateOpts}
          value={filters.state ? { value: filters.state, label: filters.state } : null}
          onChange={handleStateChange}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>District</Form.Label>
        <Select
          placeholder="Select District"
          options={districtOpts}
          value={filters.district ? { value: filters.district, label: filters.district } : null}
          onChange={handleDistrictChange}
          isDisabled={!filters.state}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Taluka</Form.Label>
        <Select
          placeholder="Select Taluka"
          options={talukaOpts}
          value={filters.taluka ? { value: filters.taluka, label: filters.taluka } : null}
          onChange={(opt) => setFilter('taluka', opt ? opt.value : '')}
          isDisabled={!filters.district}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Village</Form.Label>
        <Form.Control
          size="sm"
          placeholder="Enter village"
          value={filters.village || ''}
          onChange={e => setFilter('village', e.target.value)}
        />
      </div>
    </>
  );

  const renderDateRangeFilters = (fromKey = 'dateFrom', toKey = 'dateTo', fromLabel = 'Date From', toLabel = 'Date To') => (
    <>
      <div style={{ minWidth: 140, flex: '1 1 140px' }}>
        <Form.Label style={labelStyle}>{fromLabel}</Form.Label>
        <Form.Control size="sm" type="date" value={filters[fromKey] || ''} onChange={e => setFilter(fromKey, e.target.value)} />
      </div>
      <div style={{ minWidth: 140, flex: '1 1 140px' }}>
        <Form.Label style={labelStyle}>{toLabel}</Form.Label>
        <Form.Control size="sm" type="date" value={filters[toKey] || ''} onChange={e => setFilter(toKey, e.target.value)} />
      </div>
    </>
  );

  // ── Section filter panels ────────────────────────────────────────────────────
  const renderFarmerFilters = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
      {renderLocationFilters()}
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Crop</Form.Label>
        <Form.Control size="sm" placeholder="Enter crop name" value={filters.crop || ''} onChange={e => setFilter('crop', e.target.value)} />
      </div>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Land Acreage</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'marginal', label: '< 1 Acre (Marginal)' },
            { value: 'small',    label: '1–2 Acres (Small)' },
            { value: 'medium',   label: '2–5 Acres (Medium)' },
            { value: 'large',    label: '> 5 Acres (Large)' },
          ]}
          value={filters.landAcreage ? { value: filters.landAcreage, label: { marginal: '< 1 Acre (Marginal)', small: '1–2 Acres (Small)', medium: '2–5 Acres (Medium)', large: '> 5 Acres (Large)' }[filters.landAcreage] || '' } : null}
          onChange={opt => setFilter('landAcreage', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Farmer Category</Form.Label>
        <Select
          placeholder="All Categories"
          options={[
            { value: 'gold',   label: 'Gold Farmer' },
            { value: 'silver', label: 'Silver Farmer' },
            { value: 'bronze', label: 'Bronze Farmer' },
            { value: 'raw',    label: 'Raw Farmer' },
          ]}
          value={filters.farmerCategory ? { value: filters.farmerCategory, label: { gold: 'Gold Farmer', silver: 'Silver Farmer', bronze: 'Bronze Farmer', raw: 'Raw Farmer' }[filters.farmerCategory] || '' } : null}
          onChange={opt => setFilter('farmerCategory', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Status</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'active',     label: 'Active' },
            { value: 'non_active', label: 'Non Active' },
          ]}
          value={filters.status ? { value: filters.status, label: { active: 'Active', non_active: 'Non Active' }[filters.status] || '' } : null}
          onChange={opt => setFilter('status', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Heard About</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'Farmer',               label: 'Farmer' },
            { value: 'Promotional material', label: 'Promotional material' },
            { value: 'Staff',                label: 'Staff' },
            { value: 'Exhibition',           label: 'Exhibition' },
            { value: 'Dealer',               label: 'Dealer' },
            { value: 'Other',                label: 'Other' },
          ]}
          value={filters.heardAbout ? { value: filters.heardAbout, label: filters.heardAbout } : null}
          onChange={opt => setFilter('heardAbout', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      {renderDateRangeFilters()}
    </div>
  );

  const renderQueryFilters = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Crop</Form.Label>
        <Form.Control size="sm" placeholder="Enter crop name" value={filters.crop || ''} onChange={e => setFilter('crop', e.target.value)} />
      </div>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Season</Form.Label>
        <Select
          placeholder="All Seasons"
          options={[
            { value: 'Kharif', label: 'Kharif' },
            { value: 'Rabi',   label: 'Rabi' },
            { value: 'Zaid',   label: 'Zaid' },
          ]}
          value={filters.season ? { value: filters.season, label: filters.season } : null}
          onChange={opt => setFilter('season', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Query Category</Form.Label>
        <Form.Control size="sm" placeholder="Category" value={filters.queryCategory || ''} onChange={e => setFilter('queryCategory', e.target.value)} />
      </div>
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <Form.Label style={labelStyle}>Status</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'New',              label: 'New' },
            { value: 'In Progress',      label: 'In Progress' },
            { value: 'SolutionProvided', label: 'Solution Provided' },
            { value: 'Done',             label: 'Done' },
            { value: 'On Hold',          label: 'On Hold' },
            { value: 'In Follow',        label: 'In Follow' },
            { value: 'Rejected',         label: 'Rejected' },
          ]}
          value={filters.queryStatus ? { value: filters.queryStatus, label: filters.queryStatus } : null}
          onChange={opt => setFilter('queryStatus', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      {renderLocationFilters()}
      {renderDateRangeFilters()}
    </div>
  );

  const renderAppointmentFilters = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Staff</Form.Label>
        <Form.Control size="sm" placeholder="Staff name" value={filters.staff || ''} onChange={e => setFilter('staff', e.target.value)} />
      </div>
      <div style={{ minWidth: 150, flex: '1 1 150px' }}>
        <Form.Label style={labelStyle}>Crop</Form.Label>
        <Form.Control size="sm" placeholder="Enter crop name" value={filters.crop || ''} onChange={e => setFilter('crop', e.target.value)} />
      </div>
      {renderLocationFilters()}
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Appointment Status</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'Scheduled', label: 'Scheduled' },
            { value: 'Completed', label: 'Completed' },
            { value: 'Cancelled', label: 'Cancelled' },
            { value: 'No Show',   label: 'No Show' },
          ]}
          value={filters.appointmentStatus ? { value: filters.appointmentStatus, label: filters.appointmentStatus } : null}
          onChange={opt => setFilter('appointmentStatus', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      {renderDateRangeFilters('appointmentDateFrom', 'appointmentDateTo', 'Appt. Date From', 'Appt. Date To')}
    </div>
  );

  const renderFeedbackFilters = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
      <div style={{ minWidth: 160, flex: '1 1 160px' }}>
        <Form.Label style={labelStyle}>Feedback Category</Form.Label>
        <Form.Control size="sm" placeholder="Category" value={filters.feedbackCategory || ''} onChange={e => setFilter('feedbackCategory', e.target.value)} />
      </div>
      {renderLocationFilters()}
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <Form.Label style={labelStyle}>Advisor / Operator</Form.Label>
        <Select
          placeholder="All Advisors"
          options={operatorOpts}
          value={filters.advisor ? { value: filters.advisor, label: filters.advisor } : null}
          onChange={opt => setFilter('advisor', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      {renderDateRangeFilters()}
    </div>
  );

  const renderAdvisorFilters = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
      <div style={{ minWidth: 200, flex: '1 1 200px' }}>
        <Form.Label style={labelStyle}>Advisor / Operator</Form.Label>
        <Select
          placeholder="All Operators"
          options={operatorOpts}
          value={filters.advisor ? { value: filters.advisor, label: filters.advisor } : null}
          onChange={opt => setFilter('advisor', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <Form.Label style={labelStyle}>Queries Resolved</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'yes', label: 'Yes — Has Resolved' },
            { value: 'no',  label: 'No — None Resolved' },
          ]}
          value={filters.queriesResolved ? { value: filters.queriesResolved, label: { yes: 'Yes — Has Resolved', no: 'No — None Resolved' }[filters.queriesResolved] || '' } : null}
          onChange={opt => setFilter('queriesResolved', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <Form.Label style={labelStyle}>Appointments Taken</Form.Label>
        <Select
          placeholder="All"
          options={[
            { value: 'yes', label: 'Yes — Has Taken' },
            { value: 'no',  label: 'No — None Taken' },
          ]}
          value={filters.appointmentsTaken ? { value: filters.appointmentsTaken, label: { yes: 'Yes — Has Taken', no: 'No — None Taken' }[filters.appointmentsTaken] || '' } : null}
          onChange={opt => setFilter('appointmentsTaken', opt ? opt.value : '')}
          isClearable
          styles={selectStyles}
        />
      </div>
      {renderDateRangeFilters()}
    </div>
  );

  const renderFilterPanel = () => {
    switch (activeMenu) {
      case 'farmers':     return renderFarmerFilters();
      case 'queries':     return renderQueryFilters();
      case 'appointment': return renderAppointmentFilters();
      case 'feedback':    return renderFeedbackFilters();
      case 'advisor':     return renderAdvisorFilters();
      default:            return null;
    }
  };

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: C.page }}>

      {/* Page Header */}
      <div style={{
        backgroundColor: C.white, padding: '0.85rem 1.5rem',
        display: 'flex', alignItems: 'center',
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 1px 4px rgba(20,50,30,0.06)', flexShrink: 0,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, backgroundColor: C.light,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12,
        }}>
          <FontAwesomeIcon icon={faFileAlt} style={{ color: C.mid, fontSize: '0.9rem' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: C.textDk }}>Reports</div>
          <div style={{ fontSize: '0.72rem', color: C.textGr }}>Generate and export reports</div>
        </div>
      </div>

      {/* Body: Left Menu + Right Panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left Menu */}
        <div style={{
          width: 200, flexShrink: 0, height: '100%', overflowY: 'auto',
          backgroundColor: C.white, borderRight: `1px solid ${C.border}`,
          paddingTop: 8,
        }}>
          {MENU_ITEMS.map(item => {
            const isActive = activeMenu === item.id;
            return (
              <div
                key={item.id}
                onClick={() => handleMenuSelect(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', cursor: 'pointer',
                  backgroundColor: isActive ? C.active : 'transparent',
                  borderLeft: isActive ? `3px solid ${C.mid}` : '3px solid transparent',
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  backgroundColor: isActive ? C.light : '#f0f4f1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <FontAwesomeIcon icon={item.icon} style={{ color: isActive ? C.mid : C.textGr, fontSize: '0.82rem' }} />
                </div>
                <span style={{ fontSize: '0.85rem', fontWeight: isActive ? 700 : 500, color: isActive ? C.mid : C.textDk, flex: 1 }}>
                  {item.label}
                </span>
                {isActive && (
                  <FontAwesomeIcon icon={faChevronRight} style={{ color: C.mid, fontSize: '0.65rem' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Right Panel */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

          {/* Filter Card */}
          <div style={{
            backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
            padding: '1.25rem', marginBottom: '1.25rem',
            boxShadow: '0 1px 4px rgba(20,50,30,0.06)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontWeight: 700, fontSize: '1rem', color: C.textDk, marginBottom: '1rem',
            }}>
              <FontAwesomeIcon icon={faFilter} style={{ color: C.mid, fontSize: '0.9rem' }} />
              {menuMeta ? `${menuMeta.label} — Filters` : 'Report Filters'}
            </div>

            {!activeMenu ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '3rem 1rem', color: C.textGr,
              }}>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%', backgroundColor: C.light,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem',
                }}>
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: '1.4rem', color: '#9dbfa9' }} />
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.82rem' }}>
                  Select a report type from the left menu<br />to view applicable filters.
                </div>
              </div>
            ) : (
              renderFilterPanel()
            )}
          </div>

          {/* Action Buttons */}
          {activeMenu && (
            <div style={{
              backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
              padding: '1rem 1.25rem', marginBottom: '1.25rem',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10,
              boxShadow: '0 1px 4px rgba(20,50,30,0.06)',
            }}>
              {results && results.length > 0 && (
                <>
                  <Button
                    onClick={handleExportExcel}
                    style={{
                      backgroundColor: '#1e8449', border: 'none',
                      padding: '8px 20px', fontWeight: 600, fontSize: '0.88rem',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <FontAwesomeIcon icon={faFileExcel} /> Download Excel
                  </Button>
                  <Button
                    onClick={handleExportPdf}
                    style={{
                      backgroundColor: '#c0392b', border: 'none',
                      padding: '8px 20px', fontWeight: 600, fontSize: '0.88rem',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} /> Download PDF
                  </Button>
                </>
              )}
              <Button
                onClick={handleGenerate}
                disabled={loading}
                style={{
                  backgroundColor: loading ? '#6c757d' : C.mid, border: 'none',
                  padding: '8px 24px', fontWeight: 600, fontSize: '0.88rem',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <FontAwesomeIcon icon={faFileAlt} />
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 14px', backgroundColor: '#fdecea',
              border: '1px solid #ef9a9a', borderRadius: 6,
              color: '#c62828', fontSize: '0.82rem', marginBottom: '1rem',
            }}>
              {error}
            </div>
          )}

          {/* Results Table */}
          {results !== null && !loading && (
            <div style={{
              backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(20,50,30,0.06)', overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', backgroundColor: '#f7fbf8',
              }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: C.textDk }}>
                  {menuMeta?.label} Report —{' '}
                  <span style={{ color: C.mid }}>{results.length} record{results.length !== 1 ? 's' : ''}</span>
                </span>
              </div>

              {results.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: C.textGr, fontSize: '0.85rem' }}>
                  No records found for the selected filters.
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <Table striped bordered hover size="sm" style={{ margin: 0, fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f7fbf8' }}>
                          <th style={{ whiteSpace: 'nowrap', padding: '8px 10px' }}>#</th>
                          {columns.map(c => (
                            <th key={c.key} style={{ whiteSpace: 'nowrap', padding: '8px 10px', color: C.textDk }}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                              {(currentPage - 1) * PAGE_SIZE + idx + 1}
                            </td>
                            {columns.map(c => (
                              <td key={c.key} style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                {cellVal(row, c)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
                      <Pagination size="sm">
                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                        {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                          const n = Math.max(1, currentPage - 4) + i;
                          return n <= totalPages ? (
                            <Pagination.Item key={n} active={n === currentPage} onClick={() => setCurrentPage(n)}>{n}</Pagination.Item>
                          ) : null;
                        })}
                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportCenter;
